import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import {
  ActiveLookup,
  EmailAddress,
  EmailAddressKeys,
  EmailTemplates,
  Entity,
  EntityPermissionActivityKeys,
  LayoutGridType,
  LookupKeys,
  Lookups,
  Messages,
} from '@ag-common-lib/public-api';
import { filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { SetLoginEmailModalComponent } from '../set-login-email-modal/set-login-email-modal.component';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { AgentEmailAddressesService } from 'ag-common-svc/lib/services/agent-email-addresses.service';
import { AuthService, LookupsService } from 'ag-common-svc/public-api';
import { EmailAddressesService } from './email-addresses.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ColumnButtonClickEvent } from 'devextreme/ui/data_grid';
import { OtpService } from 'ag-common-svc/lib/services/otp.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'ag-shr-email-addresses',
  templateUrl: './email-addresses.component.html',
  styleUrls: ['./email-addresses.component.scss'],
  providers: [OtpService],
})
export class EmailAddressesComponent {
  @Input() set agentId(value) {
    this.agentId$.next(value);
  }
  @Input() agentUID: string;
  @Input() isSetAsLoginVisible: boolean = false;
  @Input() canEdit = true;
  @Input() canCreate = true;
  @Input() canDelete = true;
  @Input() type: LayoutGridType = LayoutGridType.short;

  @Output() emailAddressesChange = new EventEmitter();

  @ViewChild('emailAddressesEditorModalRef', { static: true }) emailAddressesEditorModalComponent: ModalWindowComponent;
  @ViewChild(SetLoginEmailModalComponent) setLoginEmailModalComponent: SetLoginEmailModalComponent;

  public Lookups = Lookups;
  public LookupKeys = LookupKeys;
  public BaseModelKeys = BaseModelKeys;
  public emailTypeLookup$: Observable<ActiveLookup[]>;
  protected Entity = Entity;
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;

  readonly inProgress$ = this.emailAddressesService.inProgress$;
  readonly Messages = Messages;

  private defaultEmailTypeLookup: ActiveLookup;
  private readonly agentId$ = new BehaviorSubject<string>(undefined);
  private busy = new Map<string | number, boolean>();

  emailAddresses$ = this.agentId$.pipe(
    filter(Boolean),
    switchMap((agentId: string) => this.agentEmailAddressesService.getList(agentId)),
    shareReplay(1),
  );
  emailAddressesDataSource$ = this.emailAddresses$.pipe(
    map((emailAddresses: EmailAddress[]) => {
      return new DataSource({
        store: new ArrayStore({
          key: 'dbId',
          data: Array.isArray(emailAddresses) ? emailAddresses : [],
        }),
      });
    }),
  );

  constructor(
    lookupsService: LookupsService,
    private authService: AuthService,
    private agentEmailAddressesService: AgentEmailAddressesService,
    private emailAddressesService: EmailAddressesService,
    private otpService: OtpService,
    private toastrService: ToastrService,
  ) {
    this.emailTypeLookup$ = lookupsService.emailTypeLookup$.pipe(
      tap((items: ActiveLookup[]): void => {
        this.defaultEmailTypeLookup = items?.find(item => item?.isDefault);
      }),
    );
  }

  protected isRowBusy = ($event): boolean => {
    const key = $event?.row?.key;
    console.log('isRowBusy', this.busy.get(key));

    return !!this.busy.get(key);
  };

  protected iRowNotBusy = ($event): boolean => {
    const key = $event?.row?.key;

    return !this.busy.get(key);
  };

  protected iconGetter = $event => {
    debugger;
    return 'key';
  };

  public get agentDbId(): string | null {
    return this.agentId$.value ?? null;
  }

  showEmailAddressesEditorModal = (): void => {
    this.emailAddressesEditorModalComponent.showModal();
  };

  onEditorPreparing = (e): void => {
    if (e.parentType !== 'dataRow') {
      return;
    }

    if (e.dataField === EmailAddressKeys.address) {
      e.editorOptions.disabled = e.row.data.is_login;
    }
  };

  onInitNewRow = (e): void => {
    e.data.is_login = false;
    e.data.email_type = this.defaultEmailTypeLookup?.dbId;
  };

  onRowInserting = (e): void => {
    const { __KEY__: key, ...data } = e?.data;

    e.cancel = this.emailAddressesService.createEmailAddress(this.agentId$.value, data);
  };

  onRowUpdating = (e): void => {
    e.cancel = this.emailAddressesService.updateEmailAddressChange(this.agentId$.value, e.key, e?.newData);
  };

  onRowRemoving = (e): void => {
    e.cancel = this.emailAddressesService.deleteEmailAddress(this.agentId$.value, e.key);
  };

  canDeleteEmailAddress = (e): boolean => !e.row.data.is_login;

  checkIsSetAsLoginVisible = (e): boolean => this.isSetAsLoginVisible && !e.row.data.is_login;

  showSetLoginEmailModal = (): void => {
    this.setLoginEmailModalComponent?.showModal(this.agentId$.value, this.agentUID);
  };

  asyncUniqEmailValidation = async ({ data, value }): Promise<boolean> => {
    const emailAddresses = await firstValueFrom(this.emailAddresses$);

    return !emailAddresses.some(
      (emailAddress: EmailAddress) => emailAddress?.address === value && data?.dbId !== emailAddress?.dbId,
    );
  };

  sendOneTimeCode = async ($event: ColumnButtonClickEvent) => {
    const key = $event?.row?.key;
    if (this.isRowBusy(key)) {
      return;
    }
    this.busy.set(key, true);

    $event.component.repaint();
    try {
      const rowData: EmailAddress = $event?.row?.data;
      const email = rowData?.[EmailAddressKeys.address];
      const currentUser = await firstValueFrom(this.authService.currentUser$.pipe(take(1)));
      await this.otpService.sendOtp(email, {
        bcc: [currentUser?.email],
        duration: 1440,
        emailTemplate: EmailTemplates.permanentOTP,
        isPermanent: true,
      });

      this.toastrService.info(`Confirmation code was sent to "${email}"`);
    } catch {}
    this.busy.delete(key);
    setTimeout(() => {
      $event.component.repaint();
    }, 0);
  };
}
